import React, { useEffect, useState } from "react";
import DataTable, { TableColumn } from "react-data-table-component";
import { ExternalLink, Pencil, Plus, Search, Trash2 } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { deleteProduct, getProduct } from "../../../services/admin/product";
import { getBrands } from "../../../services/admin/brand";
import { getCategory } from "../../../services/admin/category";
import { ProductData } from "../../../types/product";


interface Brand {
    id: number;
    name: string;
}

interface Category {
    id: number;
    name: string;
}

function AdminProduct() {
    const navigate = useNavigate();
    const [products, setProducts] = useState<ProductData[]>([]);
    const [brands, setBrands] = useState<Brand[]>([]);
    const [categories, setCategories] = useState<Category[]>([]);
    const [search, setSearch] = useState("");
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const fetchAll = async () => {
            setIsLoading(true);
            const [productRes, brandRes, categoryRes] = await Promise.all([
                getProduct(),
                getBrands(),
                getCategory(),
            ]);

            if (productRes.success && Array.isArray(productRes.data)) {
                setProducts(productRes.data);
            } else {
                console.error("Failed to load products", productRes.error || productRes.data);
            }

            if (brandRes.success && Array.isArray(brandRes.data)) {
                setBrands(brandRes.data);
            }

            if (categoryRes.success && Array.isArray(categoryRes.data)) {
                setCategories(categoryRes.data);
            }

            setIsLoading(false);
        };

        fetchAll();
    }, []);

    const getBrandName = (id: number) => brands.find((b) => b.id === id)?.name || "—";
    const getCategoryNames = (ids: number[] | string[]) => {
        return ids
            .map((id) => {
                const numId = Number(id); // <-- ensure number
                return categories.find((c) => c.id === numId)?.name || "—";
            })
            .join(", ");
    };

    const filteredProducts = products.filter((p) =>
        p.product_name.toLowerCase().includes(search.toLowerCase()) ||
        (p.sku && p.sku.toLowerCase().includes(search.toLowerCase()))
    ).reverse();

    const handleDelete = async (id: any) => {
        const confirmDelete = window.confirm("Are you sure you want to delete this product?");
        if (!confirmDelete) return;

        const response = await deleteProduct(id);
        if (response.success) {
            setProducts((prev) => prev.filter((product) => product.id !== id));
        } else {
            alert(response.error);
        }
    };

    const columns: TableColumn<ProductData>[] = [
        {
            name: "ID",
            cell: (_row, index) => (index + 1).toString(),
            width: "60px",
        },
        {
            name: "Product",
            selector: (row) => row.product_name,
            sortable: true,
        },
        {
            name: "Brand",
            selector: (row) => getBrandName(row.brand_id),
        },
        {
            name: "Category",
            selector: (row) => getCategoryNames(row.category_id),
        },
        {
            name: "SKU",
            selector: (row) => row.sku ?? "-",
        },
        {
            name: "Status",
            cell: (row) => (
                <span
                    className={`px-2 py-1 text-xs font-medium rounded ${row.status === "publish"
                        ? "text-green-700 bg-green-100"
                        : "text-red-700 bg-red-100"
                        }`}
                >
                    {row.status}
                </span>
            ),
        },
        {
            name: "Actions",
            cell: (row) => (
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => window.open(`/products/product-details/${row.id}`, '_blank')}
                        className="text-black hover:underline"
                        title="View"
                    >
                        <ExternalLink size={16} />
                    </button>
                    <button
                        onClick={() => navigate(`/admin/product/edit-product/${row.id}`)}
                        className="text-[#009FE3] hover:underline"
                        title="Edit"
                    >
                        <Pencil size={16} />
                    </button>
                    <button
                        onClick={() => handleDelete(row.id)}
                        className="text-red-600 hover:text-red-800 p-1"
                        title="Delete"
                    >
                        <Trash2 size={16} />
                    </button>
                </div>
            ),
            width: "150px",
        },
    ];

    return (
        <div className="p-6 space-y-6">
            <h1 className="text-2xl font-semibold text-gray-800">Products</h1>
            <div className="flex justify-between items-center flex-wrap gap-4">
                <div className="relative w-full max-w-sm">
                    <Search className="absolute left-3 top-2.5 text-gray-400 w-4 h-4" />
                    <input
                        type="text"
                        placeholder="Search products..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border rounded-lg text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#009FE3]"
                    />
                </div>

                <Link
                    to="add-product"
                    className="flex items-center gap-2 px-4 py-2 bg-[#009FE3] text-white rounded-lg hover:opacity-90 transition whitespace-nowrap"
                >
                    <Plus className="w-4 h-4" />
                    Add Product
                </Link>
            </div>
            <div className="bg-white rounded-lg shadow border">
                <DataTable
                    columns={columns}
                    data={filteredProducts}
                    progressPending={isLoading}
                    pagination
                    highlightOnHover
                    responsive
                    striped
                    noHeader
                />
            </div>
        </div>
    );
}

export default AdminProduct;
